import React, { useState } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { Formik } from 'formik';
import Button from '../../../BaseComponents/Button';
import TextInput from '../../../BaseComponents/TextInput';
import Select, { SelectOption } from '../../../BaseComponents/Select';
import { toast } from 'react-toastify';
import {
  createPmpModality,
  getPmpModalityErrorMessage,
  PmpModality,
  updatePmpModality,
} from '../../../../api/pmpModalities';
import { PMP_MODALITY_NAME_OPTIONS } from '../../../../utils/constants';
import './PmpModalityModal.scss';

type PmpModalityModalProps = {
  isOpen: boolean;
  modalityToEdit: PmpModality | null;
  onClose: () => void;
  onSaved: () => void;
};

interface ModalityFormValues {
  name: SelectOption | null;
  full_price: string;
  member_price: string;
}

const parseOptionalPrice = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const findNameOption = (name?: string | null): SelectOption | null => {
  if (!name) return null;
  return (
    PMP_MODALITY_NAME_OPTIONS.find(
      (option) => option.key === name || option.value === name
    ) ?? { key: name, value: name }
  );
};

const PmpModalityModal: React.FC<PmpModalityModalProps> = ({
  isOpen,
  modalityToEdit,
  onClose,
  onSaved,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getDefaultValues = (): ModalityFormValues => {
    if (modalityToEdit) {
      return {
        name: findNameOption(modalityToEdit.name),
        full_price:
          modalityToEdit.full_price !== null && modalityToEdit.full_price !== undefined
            ? String(modalityToEdit.full_price)
            : '',
        member_price:
          modalityToEdit.member_price !== null && modalityToEdit.member_price !== undefined
            ? String(modalityToEdit.member_price)
            : '',
      };
    }

    return {
      name: null,
      full_price: '',
      member_price: '',
    };
  };

  const defaultValues = getDefaultValues();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setErrorMessage(null);
        onClose();
      }}
      title={modalityToEdit ? 'Edit PMP Modality' : 'Add PMP Modality'}
      width='600px'
    >
      <Formik
        enableReinitialize
        initialValues={defaultValues}
        onSubmit={async (values) => {
          setIsLoading(true);
          setErrorMessage(null);

          if (!values.name?.value) {
            const message = 'Please select a modality name';
            setErrorMessage(message);
            toast.error(message);
            setIsLoading(false);
            return;
          }

          const fullPrice = parseOptionalPrice(values.full_price);
          const memberPrice = parseOptionalPrice(values.member_price);

          if (
            (values.full_price.trim() && fullPrice === null) ||
            (values.member_price.trim() && memberPrice === null)
          ) {
            const message = 'Please enter valid prices';
            setErrorMessage(message);
            toast.error(message);
            setIsLoading(false);
            return;
          }

          if (
            (fullPrice !== null && fullPrice < 0) ||
            (memberPrice !== null && memberPrice < 0)
          ) {
            const message = 'Prices cannot be negative';
            setErrorMessage(message);
            toast.error(message);
            setIsLoading(false);
            return;
          }

          try {
            const requestObject = {
              name: String(values.name.value),
              full_price: fullPrice,
              member_price: memberPrice,
            };

            if (modalityToEdit) {
              await updatePmpModality({
                ...requestObject,
                id: modalityToEdit.id,
              });
              toast.success('Modality updated successfully');
            } else {
              await createPmpModality(requestObject);
              toast.success('Modality added successfully');
            }

            setErrorMessage(null);
            onSaved();
          } catch (err: any) {
            const message = getPmpModalityErrorMessage(
              err,
              modalityToEdit
                ? 'Failed to update modality'
                : 'Failed to add modality'
            );
            setErrorMessage(message);
            toast.error(message);
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} className='pmp-modality-modal-form'>
            <Select
              name='name'
              title='Name *'
              options={PMP_MODALITY_NAME_OPTIONS}
              selectedOption={values.name}
              onChange={(option) => {
                setErrorMessage(null);
                setFieldValue('name', option);
              }}
              placeholder='Select modality'
              isError={!!errorMessage && !values.name?.value}
            />

            <TextInput
              name='full_price'
              title='Full Price'
              placeholder='Enter full price'
              type='number'
              value={values.full_price}
              onChange={(e) => {
                if (e.target.value.includes('-')) return;
                setErrorMessage(null);
                setFieldValue('full_price', e.target.value);
              }}
              isError={false}
            />

            <TextInput
              name='member_price'
              title='Member Price'
              placeholder='Enter member price'
              type='number'
              value={values.member_price}
              onChange={(e) => {
                if (e.target.value.includes('-')) return;
                setErrorMessage(null);
                setFieldValue('member_price', e.target.value);
              }}
              isError={false}
            />

            <div className='btn-wrapper'>
              <Button
                text={modalityToEdit ? 'Save changes' : 'Add Modality'}
                type='submit'
                isDisabled={!values.name?.value}
                isLoading={isLoading}
              />

              {errorMessage && (
                <div className='error-message'>{errorMessage}</div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default PmpModalityModal;
